import { z } from 'zod';

import { getFieldRegistryEntry } from '../../config/configFieldRegistry';
import { addScopePrefix } from '../../util/userHelpers';

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 256;

export { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH };

/**
 * Intl-like shape accepted by schema factories.
 * Matches the subset of react-intl's IntlShape we actually need.
 */
interface IntlLike {
  formatMessage: (descriptor: { id: string }, values?: Record<string, any>) => string;
}

// ── Login ──

export const createLoginSchema = (intl: IntlLike) =>
  z.object({
    email: z
      .string()
      .min(1, intl.formatMessage({ id: 'LoginForm.emailRequired' }))
      .email(intl.formatMessage({ id: 'LoginForm.emailInvalid' })),
    password: z
      .string()
      .min(1, intl.formatMessage({ id: 'LoginForm.passwordRequired' })),
  });

export type LoginFormValues = z.infer<ReturnType<typeof createLoginSchema>>;

// ── Signup account step ──

export const createAccountStepSchema = (intl: IntlLike) =>
  z.object({
    email: z
      .string()
      .min(1, intl.formatMessage({ id: 'SignupForm.emailRequired' }))
      .email(intl.formatMessage({ id: 'SignupForm.emailInvalid' })),
    password: z
      .string()
      .min(
        PASSWORD_MIN_LENGTH,
        intl.formatMessage({ id: 'SignupForm.passwordTooShort' }, { minLength: PASSWORD_MIN_LENGTH })
      )
      .max(
        PASSWORD_MAX_LENGTH,
        intl.formatMessage({ id: 'SignupForm.passwordTooLong' }, { maxLength: PASSWORD_MAX_LENGTH })
      ),
    firstName: z
      .string()
      .min(1, intl.formatMessage({ id: 'SignupForm.firstNameRequired' }))
      .transform(v => v.trim()),
    lastName: z
      .string()
      .min(1, intl.formatMessage({ id: 'SignupForm.lastNameRequired' }))
      .transform(v => v.trim()),
  });

export type AccountStepValues = z.infer<ReturnType<typeof createAccountStepSchema>>;

// ── Password recovery ──

export const createPasswordRecoverySchema = (intl: IntlLike) =>
  z.object({
    email: z
      .string()
      .min(1, intl.formatMessage({ id: 'PasswordRecoveryForm.emailRequired' }))
      .email(intl.formatMessage({ id: 'PasswordRecoveryForm.emailInvalid' })),
  });

export type PasswordRecoveryValues = z.infer<ReturnType<typeof createPasswordRecoverySchema>>;

// ── Password reset ──

export const createPasswordResetSchema = (intl: IntlLike) =>
  z.object({
    newPassword: z
      .string()
      .min(
        PASSWORD_MIN_LENGTH,
        intl.formatMessage({ id: 'PasswordResetForm.passwordTooShort' }, { minLength: PASSWORD_MIN_LENGTH })
      )
      .max(
        PASSWORD_MAX_LENGTH,
        intl.formatMessage({ id: 'PasswordResetForm.passwordTooLong' }, { maxLength: PASSWORD_MAX_LENGTH })
      ),
  });

export type PasswordResetValues = z.infer<ReturnType<typeof createPasswordResetSchema>>;

// ────────────────────────────────────────────
// Dynamic schema builder for custom user fields
// ────────────────────────────────────────────

export interface UserFieldConfig {
  key: string;
  scope?: string;
  schemaType: string;
  /** Top-level label (used in hosted asset JSON). */
  label?: string;
  enumOptions?: Array<{ option: string; label: string }>;
  numberConfig?: { minimum?: number; maximum?: number };
  saveConfig?: {
    label?: string;
    placeholderMessage?: string;
    isRequired?: boolean;
    required?: boolean; // hosted asset variant
    requiredMessage?: string;
    displayInSignUp?: boolean;
  };
  userTypeConfig?: {
    limitToUserTypeIds?: boolean;
    userTypeIds?: string[];
  };
  showConfig?: {
    label?: string;
    displayInProfile?: boolean;
  };
}

/**
 * Build a default Zod schema for a field based on its schemaType.
 * Registry overrides take priority when present.
 */
const buildDefaultSchema = (field: UserFieldConfig, isRequired: boolean): z.ZodTypeAny => {
  const label = field.saveConfig?.label || field.label || field.showConfig?.label || field.key;
  const requiredMsg = field.saveConfig?.requiredMessage || `${label} is required`;

  switch (field.schemaType) {
    case 'enum':
      return isRequired
        ? z.string().min(1, requiredMsg)
        : z.string().optional();

    case 'multi-enum':
      return isRequired
        ? z.array(z.string()).min(1, requiredMsg)
        : z.array(z.string()).optional();

    case 'text':
      return isRequired
        ? z.string().min(1, requiredMsg)
        : z.string().optional();

    case 'long':
      return isRequired
        ? z.coerce.number({ invalid_type_error: `${label} must be a number` })
        : z.coerce.number().optional();

    case 'boolean':
      return z.boolean().optional();

    case 'youtubeVideoUrl':
      return isRequired
        ? z.string().min(1, requiredMsg).url('Please enter a valid URL')
        : z.string().url('Please enter a valid URL').optional().or(z.literal(''));

    default:
      return isRequired
        ? z.string().min(1, requiredMsg)
        : z.string().optional();
  }
};

/**
 * Build a Zod object schema from an array of user field configs.
 * Keys are namespaced (e.g. `pub_fieldKey`) to match the form field names
 * used by `pickUserFieldsData`.
 */
export const buildCustomFieldsSchema = (fields: UserFieldConfig[]): z.ZodObject<any> => {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    const scope = field.scope || 'public';
    const namespacedKey = addScopePrefix(scope, field.key);
    const isRequired = !!(field.saveConfig?.isRequired || field.saveConfig?.required);

    // Check registry for a custom schema override
    const registryEntry = getFieldRegistryEntry(field.key);

    if (registryEntry?.zodSchema) {
      // Use registry schema, but make it optional if the field isn't required
      shape[namespacedKey] = isRequired
        ? registryEntry.zodSchema
        : registryEntry.zodSchema.optional();
    } else {
      shape[namespacedKey] = buildDefaultSchema(field, isRequired);
    }
  }

  return z.object(shape);
};
